import type { UIRouterGlobals } from "@uirouter/angularjs";
import type { IScope } from "angular";
import { cloneDeep, filter, isNil, map as _map, some } from "lodash";
import { finalize, map } from "rxjs/operators";

import { zip } from "rxjs";

import type {
    IDirectConnection,
    IEquipment,
    IEquipmentDetails,
    IEquipmentFunctionalityType,
    IEquipmentSlim,
    IEquipmentTypeSlim,
    IResponseWithLoading,
    ISiteEquipmentWithCoverages,
} from "App/Modules/Models";
import { EquipmentTypeFunctions } from "App/Modules/Models";
import type {
    IEquipmentRepository,
    IEquipmentTypeRepository,
    ISiteEquipmentRepository,
} from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class ConnectSiteEquipmentToHardwareComponentController {
    public static $inject: string[] = [
        "$scope",
        "$uiRouterGlobals",
        "$translate",
        "mmEquipmentRepository",
        "mmEquipmentTypeRepository",
        "mmSiteEquipmentRepository",
    ];

    public equipmentSuggestionList: IResponseWithLoading<IEquipmentSlim[]>;

    public newEquipmentToConnect: IEquipmentSlim;

    public equipmentTypes: IEquipmentTypeSlim[];

    public existingEquipments: IEquipmentSlim[];

    public isNew: boolean;

    public isOnCurrentSite: boolean;

    public isOnSite: boolean;

    public isEquipmentTypeCompatible: boolean;

    public originalSiteEquipment: ISiteEquipmentWithCoverages;

    public siteEquipment: ISiteEquipmentWithCoverages;

    public siteId: string;

    public directConnections: IResponseWithLoading<IDirectConnection[]>;

    public equipmentFunctionalityTypes: IResponseWithLoading<IEquipmentFunctionalityType[]>;

    public resolve: IFormContainerResolve<ISiteEquipmentWithCoverages, ISiteEquipmentWithCoverages>;

    public connectResponse: IResponseWithLoading<ISiteEquipmentWithCoverages>;

    constructor(
        private readonly $scope: IScope,
        private readonly $uiRouterGlobals: UIRouterGlobals,
        private readonly $translate: ng.translate.ITranslateService,
        private readonly equipmentRepository: IEquipmentRepository,
        private readonly equipmentTypeRepository: IEquipmentTypeRepository,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
    ) {
        this.isEquipmentTypeCompatible = true;
    }

    public $onInit() {
        this.originalSiteEquipment = this.resolve.bindings;
        this.siteEquipment = cloneDeep(this.originalSiteEquipment);
        this.newEquipmentToConnect = {} as IEquipment;

        this.siteId = this.$uiRouterGlobals.params.siteId;

        this.equipmentTypeRepository
            .getEquipmentTypes()
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response) => {
                this.equipmentTypes = response.data.filter((et) => et.class === this.originalSiteEquipment.class);
            });

        this.equipmentRepository
            .getEquipmentsForSite(this.siteId)
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response) => {
                this.existingEquipments = response.data;
            });

        this.$scope.$watch("$ctrl.siteEquipment.equipment.type", (newValue: IEquipmentTypeSlim) => {
            this.checkEquipmentTypeCompatibility(newValue);
        });

        this.$scope.$watch("$ctrl.equipmentToConnect.type", (newValue: IEquipmentTypeSlim) => {
            this.checkEquipmentTypeCompatibility(newValue);
        });
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public equipmentSearch(search: string) {
        this.isOnSite = false;
        this.isOnCurrentSite = false;

        return extendWithLoadingAndFail(this.equipmentRepository.getEquipment(search))
            .pipe(
                map((response) => {
                    this.equipmentSuggestionList = response;

                    const createNewDisplayName = `${this.$translate.instant("Gui.Common.CreateNew")} ${search}`;
                    const createNew = {
                        displayName: createNewDisplayName,
                        serialNumber: createNewDisplayName,
                        type: {
                            class: this.siteEquipment.className,
                        },
                    };

                    // Remove existing equipments from search result
                    response.data = filter(response.data, (e) => !this.existingEquipments.find((x) => x.id === e.id));

                    response.data = filter(response.data, (e) => e.type.class === this.siteEquipment.class);
                    response.data = _map(response.data, (e) => {
                        const equipmentType: string = this.$translate.instant(
                            `Enums.EquipmentClass.${e.type.className}`,
                        );
                        e.serialNumber = e.serialNumber.trim();
                        e.displayName =
                            (e.serialNumber || "") +
                            (e.type.className
                                ? `, ${e.type.manufacturer.name} ${e.type.model} (${equipmentType})`
                                : "");
                        return e;
                    });
                    response.data = response.data.slice(0, 7);
                    response.data.push(createNew as any);

                    return response.data;
                }),
            )
            .toPromise();
    }

    public equipmentSelected($item: IEquipment) {
        if ($item.id == null) {
            this.isNew = true;
            this.isOnSite = false;

            const createNewTranslation = this.$translate.instant("Gui.Common.CreateNew");
            this.newEquipmentToConnect.serialNumber = $item.serialNumber.replace(createNewTranslation, "").trim();
        } else {
            this.isNew = false;

            this.equipmentRepository.getSitesForEquipment($item.id).subscribe((response) => {
                if (response.data && response.data.length > 0) {
                    if (some(response.data, (item) => item.id === this.siteId)) {
                        this.isOnCurrentSite = true;
                    } else {
                        this.isOnSite = true;
                    }
                }
                throw new Error("Code above changed due to tslint errors - please check!");
            });
        }
    }

    public connectToEquipment() {
        if (
            (this.directConnections && this.directConnections.isLoading) ||
            (this.equipmentFunctionalityTypes && this.equipmentFunctionalityTypes.isLoading)
        ) {
            return;
        }

        if (!this.isNew && !isNil(this.siteEquipment.equipment) && !isNil(this.siteEquipment.equipment.id)) {
            const patchObject = {
                equipment: {
                    id: this.siteEquipment.equipment.id,
                    type: {
                        id: this.siteEquipment.equipment.type.id,
                    },
                },
            } as ISiteEquipmentWithCoverages;

            extendWithLoadingAndFail(
                this.siteEquipmentRepository.patch(this.siteId, this.siteEquipment.id, patchObject),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<ISiteEquipmentWithCoverages>) => {
                        this.connectResponse = response;
                        if (!response.isLoading && response.data) {
                            this.resolve.options.onOk(response.data);
                        }
                    },
                    (error) => {
                        this.connectResponse = error;
                    },
                );
        }

        if (this.isNew) {
            const patchObject = {
                equipment: {
                    originalSerialNumber: this.newEquipmentToConnect.originalSerialNumber,
                    serialNumber: this.newEquipmentToConnect.serialNumber,
                    type: {
                        id: this.newEquipmentToConnect.type.id,
                    },
                } as IEquipmentDetails,
            } as ISiteEquipmentWithCoverages;

            extendWithLoadingAndFail(
                this.siteEquipmentRepository.patch(this.siteId, this.siteEquipment.id, patchObject),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<ISiteEquipmentWithCoverages>) => {
                        this.connectResponse = response;
                        if (!response.isLoading && response.data) {
                            this.resolve.options.onOk(response.data);
                        }
                    },
                    (error) => {
                        this.connectResponse = error;
                    },
                );
        }
    }

    private checkEquipmentTypeCompatibility(newValue: IEquipmentTypeSlim) {
        this.isEquipmentTypeCompatible = true;

        if (isNil(newValue) || isNil(newValue.id)) {
            return;
        }

        zip(
            extendWithLoadingAndFail(
                this.siteEquipmentRepository.getDirectConnections(this.siteId, this.siteEquipment.id),
            ),
            extendWithLoadingAndFail(this.equipmentTypeRepository.getEquipmentFunctionalityTypes(newValue.id)),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                ([directConnections, equipmentFuncTypes]) => {
                    this.directConnections = directConnections;
                    this.equipmentFunctionalityTypes = equipmentFuncTypes;

                    if (!directConnections.isLoading && !equipmentFuncTypes.isLoading) {
                        this.isEquipmentTypeCompatible = EquipmentTypeFunctions.checkEquipmentTypeCompability(
                            directConnections.data,
                            equipmentFuncTypes.data,
                        );
                    }
                },
                ([directConnections, equipmentFuncTypes]) => {
                    this.directConnections = directConnections;
                    this.equipmentFunctionalityTypes = equipmentFuncTypes;
                },
            );
    }
}
